import type { ImageData } from '@/frontend-types'
import { FontAwesomeIcon } from '@fortawesome/react-fontawesome'
import { cx } from 'cva'
import Link from 'next/link'
import type { ComponentProps, FC } from 'react'
import { ImageWithCorner } from './ImageWithCorner'
import { PlaceholderImage } from './PlaceholderImage'

export type CardVideoType = {
	id: string
	image?: ImageData
	label: string
	title: string
	date: string
	href: string
	video: boolean
}

export type CardVideoProps = Omit<ComponentProps<typeof Link>, 'href'> & {
	card: CardVideoType
}

export const CardVideo: FC<CardVideoProps> = ({ card, ...props }) => {
	const { video, image, label, title, date, href } = card
	return (
		<Link href={href} target="_self" className="flex flex-col gap-y-md group" {...props}>
			<div className="relative">
				{image ? (
					<ImageWithCorner
						image={image}
						aspectRatio="aspect-card-video"
						hover
						sizes="(max-width: 767px) 100vw, (max-width: 1279px) 50vw, (max-width: 1559px) 33.33vw, 456px"
					/>
				) : (
					<PlaceholderImage />
				)}
				{video && (
					<div className="absolute inset-0 flex items-center justify-center">
						{/* Sizing different to other icon */}
						<div
							className="flex h-14.5 w-14.5 items-center justify-center rounded-full bg-blue text-white group-hover:bg-blue-shade-40 group-hover:text-white group-active:bg-blue-shade-70 group-active:text-white tablet-l:h-20 tablet-l:w-20"
							aria-hidden="true"
						>
							<FontAwesomeIcon
								icon={['fal', 'play']}
								className="text-[length:var(--size-icon-xl)] tablet-l:text-[length:var(--size-icon-2xl)]"
							/>
						</div>
					</div>
				)}
			</div>
			<div className="relative flex flex-1 flex-col pt-xl pr-xl tablet-l:w-full">
				<div className="flex flex-col gap-y-xs">
					<span className="text-h5 text-blue-shade-70">{label}</span>
					<span className="text-h4 text-blue group-hover:text-blue-shade-40 group-active:text-blue-shade-70">
						{title}
					</span>
				</div>
				<div className="mt-auto flex pt-4xl pb-xl">
					<span className="text-body-sm text-blue-shade-70">{date}</span>
				</div>
				<div
					className={cx(
						'absolute right-0 bottom-0 h-(length:--stroke-sm) bg-blue left-0 pointer-events-none transition-all ease-in-out duration-150 z-15',
						'group-hover:bg-blue-shade-40 group-active:bg-blue-shade-70 group-hover:h-(length:--stroke-md)',
					)}
					aria-hidden="true"
				/>
			</div>
		</Link>
	)
}
